import { Type, type Static } from "@sinclair/typebox";
import { Cents, TripKind, TripStatus, RideCategory } from "./domain.ts";

/**
 * Support — Ajuda (Cliente-12), Suporte chat (Cliente-13), "Ajuda para motoristas", Central de
 * segurança, "Relatar um incidente"; staff inbox in admin (module `suporte`).
 */

export const SupportApp = Type.Union([Type.Literal("rider"), Type.Literal("driver")], { $id: "SupportApp" });
export type SupportApp = Static<typeof SupportApp>;

/** Topic ids. `seguranca` exists in both apps (urgent for drivers). */
export const SUPPORT_TOPIC_IDS = [
  "cobranca", "objeto", "motorista", "pagamentos", "conta", "seguranca", "acessibilidade",
  "ganhos", "documentos", "passageiro", "app", "incidente",
] as const;
export const SupportTopicId = Type.Union(
  [
    Type.Literal("cobranca"), Type.Literal("objeto"), Type.Literal("motorista"), Type.Literal("pagamentos"), Type.Literal("conta"),
    Type.Literal("seguranca"), Type.Literal("acessibilidade"), Type.Literal("ganhos"), Type.Literal("documentos"), Type.Literal("passageiro"),
    Type.Literal("app"), Type.Literal("incidente"),
  ],
  { $id: "SupportTopicId" },
);
export type SupportTopicId = (typeof SUPPORT_TOPIC_IDS)[number];

/**
 * Topics per app, in display order. `icon` is a short semantic name the apps map to their own
 * symbol (receipt, bag, person, pix, account, shield, accessibility, money, document, phone, alert).
 */
export const SUPPORT_TOPICS: Record<SupportApp, readonly { id: SupportTopicId; title: string; icon: string; priority: "normal" | "urgent" }[]> = {
  rider: [
    { id: "cobranca", title: "Cobrança diferente", icon: "receipt", priority: "normal" },
    { id: "objeto", title: "Esqueci um objeto", icon: "bag", priority: "normal" },
    { id: "motorista", title: "Problema com o motorista", icon: "person", priority: "normal" },
    { id: "pagamentos", title: "Pagamentos, Pix e estornos", icon: "pix", priority: "normal" },
    { id: "conta", title: "Conta e formas de entrar", icon: "account", priority: "normal" },
    { id: "seguranca", title: "Segurança e privacidade", icon: "shield", priority: "normal" },
    { id: "acessibilidade", title: "Acessibilidade", icon: "accessibility", priority: "normal" },
  ],
  driver: [
    { id: "ganhos", title: "Ganhos e repasses", icon: "money", priority: "normal" },
    { id: "documentos", title: "Documentos e cadastro", icon: "document", priority: "normal" },
    { id: "passageiro", title: "Problema com passageiro", icon: "person", priority: "normal" },
    { id: "app", title: "Problemas no app", icon: "phone", priority: "normal" },
    { id: "seguranca", title: "Central de segurança", icon: "shield", priority: "urgent" },
    { id: "incidente", title: "Relatar um incidente", icon: "alert", priority: "normal" },
    { id: "conta", title: "Conta e privacidade", icon: "account", priority: "normal" },
  ],
};

/** Topic chips under "Sua última corrida" (Cliente-12). */
export const SUPPORT_QUICK_TOPICS: Record<SupportApp, readonly SupportTopicId[]> = {
  rider: ["cobranca", "objeto", "motorista"],
  driver: ["ganhos", "passageiro", "incidente"],
};

export const SupportTopic = Type.Object({ id: SupportTopicId, title: Type.String(), icon: Type.String() }, { $id: "SupportTopic" });
export const SupportArticle = Type.Object(
  { id: Type.String(), topic: SupportTopicId, title: Type.String(), body: Type.String({ description: "Plain text, paragraphs separated by blank lines" }) },
  { $id: "SupportArticle" },
);
export type SupportArticle = Static<typeof SupportArticle>;

export const SupportTicketStatus = Type.Union(
  [Type.Literal("open"), Type.Literal("waiting_staff"), Type.Literal("waiting_user"), Type.Literal("closed")],
  { $id: "SupportTicketStatus" },
);
export type SupportTicketStatus = Static<typeof SupportTicketStatus>;
export const SupportPriority = Type.Union([Type.Literal("normal"), Type.Literal("urgent")], { $id: "SupportPriority" });
export const SupportSender = Type.Union([Type.Literal("user"), Type.Literal("staff"), Type.Literal("bot"), Type.Literal("system")], { $id: "SupportSender" });
export type SupportSender = Static<typeof SupportSender>;

// ── Cards inside messages ────────────────────────────────────────────────────────────────

/** "Congonhas · R$ 27,90 · Pix · 11,4 km". */
export const SupportTripCard = Type.Object(
  {
    kind: Type.Literal("trip"),
    tripId: Type.String(),
    title: Type.String({ description: "Dropoff short title, e.g. \"Aeroporto de Congonhas\"" }),
    fareCents: Cents,
    distanceMeters: Type.Integer(),
    code: Type.Optional(Type.String()),
    completedAt: Type.Optional(Type.Union([Type.String({ format: "date-time" }), Type.Null()])),
  },
  { $id: "SupportTripCard" },
);
export const RefundOfferStatus = Type.Union([Type.Literal("pending"), Type.Literal("accepted"), Type.Literal("expired")], { $id: "RefundOfferStatus" });
/** "Reembolso via Pix R$ 6,40" + "Aceitar reembolso". Accepting queues a refund to the account that paid the Pix. */
export const SupportRefundOfferCard = Type.Object(
  { kind: Type.Literal("refund_offer"), amountCents: Cents, status: RefundOfferStatus, note: Type.String() },
  { $id: "SupportRefundOfferCard" },
);
/** "Camila, da equipe ichegou, entrou na conversa". */
export const SupportSystemCard = Type.Object({ kind: Type.Literal("system"), text: Type.String() }, { $id: "SupportSystemCard" });
export const SupportCard = Type.Union([SupportTripCard, SupportRefundOfferCard, SupportSystemCard], { $id: "SupportCard" });
export type SupportCard = Static<typeof SupportCard>;

export const SupportAttachment = Type.Object(
  { url: Type.String({ description: "Short-lived signed URL" }), contentType: Type.String() },
  { $id: "SupportAttachment" },
);

export const SupportMessageView = Type.Object(
  {
    id: Type.String(),
    ticketId: Type.String(),
    sender: SupportSender,
    /** First name of the staff member for `sender: "staff"` ("Camila"). */
    staffName: Type.Union([Type.String(), Type.Null()]),
    body: Type.String(),
    attachment: Type.Union([SupportAttachment, Type.Null()]),
    card: Type.Union([SupportCard, Type.Null()]),
    createdAt: Type.String({ format: "date-time" }),
  },
  { $id: "SupportMessageView" },
);
export type SupportMessageView = Static<typeof SupportMessageView>;

// ── Tickets ──────────────────────────────────────────────────────────────────────────────

export const SupportLastMessage = Type.Object(
  { body: Type.String(), sender: SupportSender, createdAt: Type.String({ format: "date-time" }) },
  { $id: "SupportLastMessage" },
);

export const SupportTicketSummary = Type.Object(
  {
    id: Type.String(),
    protocol: Type.String({ description: "YYYYMMDD-NNNN" }),
    topic: SupportTopicId,
    topicTitle: Type.String(),
    status: SupportTicketStatus,
    tripCode: Type.Union([Type.String(), Type.Null()]),
    lastMessage: Type.Union([SupportLastMessage, Type.Null()]),
    createdAt: Type.String({ format: "date-time" }),
    updatedAt: Type.String({ format: "date-time" }),
  },
  { $id: "SupportTicketSummary" },
);
export type SupportTicketSummary = Static<typeof SupportTicketSummary>;

export const SupportTicketTrip = Type.Object(
  {
    id: Type.String(),
    code: Type.String(),
    kind: TripKind,
    title: Type.String(),
    fareCents: Cents,
    distanceMeters: Type.Integer(),
    completedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
  },
  { $id: "SupportTicketTrip" },
);

export const SupportTicketView = Type.Object(
  {
    id: Type.String(),
    protocol: Type.String(),
    app: SupportApp,
    topic: SupportTopicId,
    topicTitle: Type.String(),
    status: SupportTicketStatus,
    priority: SupportPriority,
    trip: Type.Union([SupportTicketTrip, Type.Null()]),
    messages: Type.Array(SupportMessageView),
    /** Staff member who joined the conversation. */
    staffName: Type.Union([Type.String(), Type.Null()]),
    createdAt: Type.String({ format: "date-time" }),
    closedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
  },
  { $id: "SupportTicketView" },
);
export type SupportTicketView = Static<typeof SupportTicketView>;

export const SupportLastTrip = Type.Object(
  {
    id: Type.String(),
    code: Type.String(),
    completedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
    dropoffTitle: Type.String(),
    fareCents: Cents,
  },
  { $id: "SupportLastTrip" },
);

export const SupportHomeView = Type.Object(
  {
    topics: Type.Array(SupportTopic),
    lastTrip: Type.Union([SupportLastTrip, Type.Null()]),
    quickTopics: Type.Array(SupportTopicId),
    openTickets: Type.Array(SupportTicketSummary),
  },
  { $id: "SupportHomeView" },
);

export const CreateSupportTicketRequest = Type.Object(
  {
    topic: SupportTopicId,
    tripId: Type.Optional(Type.String()),
    message: Type.Optional(Type.String({ minLength: 1, maxLength: 2000 })),
    app: SupportApp,
  },
  { $id: "CreateSupportTicketRequest" },
);
export const SupportMessageRequest = Type.Object(
  { id: Type.String({ format: "uuid", description: "Client-generated; retries with the same id are no-ops" }), body: Type.String({ minLength: 1, maxLength: 2000 }) },
  { $id: "SupportMessageRequest" },
);

// ── Staff (admin, module `suporte`) ──────────────────────────────────────────────────────

export const AdminSupportTicketItem = Type.Object(
  {
    id: Type.String(),
    protocol: Type.String(),
    app: SupportApp,
    topic: SupportTopicId,
    topicTitle: Type.String(),
    status: SupportTicketStatus,
    priority: SupportPriority,
    cityId: Type.Union([Type.String(), Type.Null()]),
    user: Type.Object({ firstName: Type.String(), phoneMasked: Type.Union([Type.String(), Type.Null()]) }),
    tripCode: Type.Union([Type.String(), Type.Null()]),
    lastMessage: Type.Union([SupportLastMessage, Type.Null()]),
    /** Seconds since the user's last unanswered message (status waiting_staff / open), else null. */
    waitingSeconds: Type.Union([Type.Integer(), Type.Null()]),
    assignedStaff: Type.Union([Type.Object({ id: Type.String(), name: Type.String() }), Type.Null()]),
    createdAt: Type.String({ format: "date-time" }),
    updatedAt: Type.String({ format: "date-time" }),
  },
  { $id: "AdminSupportTicketItem" },
);
export const AdminSupportTicketList = Type.Object(
  {
    items: Type.Array(AdminSupportTicketItem),
    nextCursor: Type.Union([Type.String(), Type.Null()]),
    counts: Type.Object({ open: Type.Integer(), waiting_staff: Type.Integer(), waiting_user: Type.Integer(), closed: Type.Integer(), urgent: Type.Integer() }),
  },
  { $id: "AdminSupportTicketList" },
);

export const AdminSupportTicketDetail = Type.Object(
  {
    id: Type.String(),
    protocol: Type.String(),
    app: SupportApp,
    topic: SupportTopicId,
    topicTitle: Type.String(),
    status: SupportTicketStatus,
    priority: SupportPriority,
    cityId: Type.Union([Type.String(), Type.Null()]),
    assignedStaff: Type.Union([Type.Object({ id: Type.String(), name: Type.String() }), Type.Null()]),
    messages: Type.Array(SupportMessageView),
    user: Type.Object({
      id: Type.String(),
      name: Type.String(),
      phoneMasked: Type.Union([Type.String(), Type.Null()]),
      rating: Type.Number(),
      completedTrips: Type.Integer(),
      memberSince: Type.String({ format: "date-time" }),
    }),
    trip: Type.Union([
      Type.Object({
        id: Type.String(),
        code: Type.String(),
        kind: TripKind,
        status: TripStatus,
        category: RideCategory,
        pickupAddress: Type.String(),
        dropoffAddress: Type.String(),
        fareCents: Cents,
        distanceMeters: Type.Integer(),
        requestedAt: Type.String({ format: "date-time" }),
        completedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
        driverName: Type.Union([Type.String(), Type.Null()]),
        payment: Type.Union([Type.Object({ status: Type.String(), amountCents: Cents, refundedCents: Cents }), Type.Null()]),
        /** Most a refund offer may be: what the rider paid minus refunds already made or offered. */
        refundableCents: Cents,
      }),
      Type.Null(),
    ]),
    createdAt: Type.String({ format: "date-time" }),
    closedAt: Type.Union([Type.String({ format: "date-time" }), Type.Null()]),
  },
  { $id: "AdminSupportTicketDetail" },
);

export const StaffSupportMessageRequest = Type.Object({ body: Type.String({ minLength: 1, maxLength: 2000 }) }, { $id: "StaffSupportMessageRequest" });
export const RefundOfferRequest = Type.Object(
  { amountCents: Type.Integer({ minimum: 1 }), note: Type.String({ minLength: 1, maxLength: 300 }) },
  { $id: "RefundOfferRequest" },
);
